import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import departmentService from "./departmentService";

const EMPTY_FORM = {
  name: "",
  code: "",
  manager_name: "",
  manager_email: "",
  location: "",
  employees_count: 0,
  budget: 0,
  status: "active",
  description: "",
};

const INITIAL_STATS = {
  total_departments: 0,
  active_departments: 0,
  inactive_departments: 0,
  total_employees: 0,
  total_budget: 0,
};

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong. Please try again.";

const getValidationErrors = (error) =>
  error?.response?.data?.errors || {};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const formatDate = (date) => {
  if (!date) return "—";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return "—";

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const DepartmentManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [statistics, setStatistics] = useState(INITIAL_STATS);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [editingDepartment, setEditingDepartment] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const [requestVersion, setRequestVersion] = useState(0);

  const refreshData = useCallback(() => {
    setRequestVersion((version) => version + 1);
  }, []);

  // Fetch the department list from Laravel.
  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await departmentService.getAll({
        search: searchTerm.trim() || undefined,
        status: statusFilter || undefined,
        page: currentPage,
        per_page: perPage,
      });

      setDepartments(Array.isArray(response.data) ? response.data : []);

      if (response.pagination) {
        setPagination(response.pagination);
      } else {
        setPagination({
          current_page: 1,
          last_page: 1,
          per_page: perPage,
          total: Array.isArray(response.data) ? response.data.length : 0,
        });
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, currentPage, perPage, requestVersion]);

  // Fetch dashboard statistics independently.
  const fetchStatistics = useCallback(async () => {
    setStatsLoading(true);

    try {
      const response = await departmentService.getStatistics();

      if (response.success && response.data) {
        setStatistics({
          ...INITIAL_STATS,
          ...response.data,
        });
      }
    } catch (requestError) {
      console.error("Unable to fetch department statistics:", requestError);
    } finally {
      setStatsLoading(false);
    }
  }, [requestVersion]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const stats = useMemo(
    () => [
      {
        label: "Total Departments",
        value: statistics.total_departments,
        icon: "Building2",
        description: "All registered departments",
      },
      {
        label: "Active Departments",
        value: statistics.active_departments,
        icon: "CheckCircle2",
        description: "Currently active",
      },
      {
        label: "Total Employees",
        value: statistics.total_employees,
        icon: "Users",
        description: "Employees across departments",
      },
      {
        label: "Total Budget",
        value: formatCurrency(statistics.total_budget),
        icon: "Wallet",
        description: "Combined department budget",
      },
    ],
    [statistics]
  );

  const openCreateForm = () => {
    setEditingDepartment(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setError("");
    setNotice("");
    setIsFormOpen(true);
  };

  const openEditForm = (department) => {
    setEditingDepartment(department);

    setForm({
      ...EMPTY_FORM,
      ...department,
      employees_count: department.employees_count ?? 0,
      budget: department.budget ?? 0,
      status: department.status || "active",
      description: department.description || "",
    });

    setFormErrors({});
    setError("");
    setNotice("");
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;

    setIsFormOpen(false);
    setEditingDepartment(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setFormErrors((previous) => ({
      ...previous,
      [name]: undefined,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSaving(true);
    setError("");
    setNotice("");
    setFormErrors({});

    const payload = {
      name: form.name.trim(),
      code: form.code.trim(),
      manager_name: form.manager_name.trim() || null,
      manager_email: form.manager_email.trim() || null,
      location: form.location.trim() || null,
      employees_count: Number(form.employees_count || 0),
      budget: Number(form.budget || 0),
      status: form.status,
      description: form.description.trim() || null,
    };

    try {
      if (editingDepartment) {
        await departmentService.update(editingDepartment.id, payload);
        setNotice("Department updated successfully.");
      } else {
        await departmentService.create(payload);
        setNotice("Department created successfully.");
      }

      setIsFormOpen(false);
      setEditingDepartment(null);
      setForm({ ...EMPTY_FORM });
      setFormErrors({});

      // Return to the first page after adding a new department.
      if (!editingDepartment && currentPage !== 1) {
        setCurrentPage(1);
      } else {
        refreshData();
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      setFormErrors(getValidationErrors(requestError));
    } finally {
      setSaving(false);
    }
  };

  const openDetails = async (department) => {
    setSelectedDepartment(department);
    setIsDetailsOpen(true);
    setError("");

    try {
      const response = await departmentService.getById(department.id);

      if (response.data) {
        setSelectedDepartment(response.data);
      }
    } catch (requestError) {
      console.error("Unable to load department details:", requestError);
    }
  };

  const askDelete = (department) => {
    setDepartmentToDelete(department);
    setIsDeleteOpen(true);
    setError("");
  };

  const handleDelete = async () => {
    if (!departmentToDelete) return;

    setDeleting(true);
    setError("");
    setNotice("");

    try {
      await departmentService.delete(departmentToDelete.id);

      setIsDeleteOpen(false);
      setNotice("Department deleted successfully.");
      setDepartmentToDelete(null);

      if (departments.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      } else {
        refreshData();
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      setIsDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handlePerPageChange = (event) => {
    setPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const startItem =
    pagination.total === 0
      ? 0
      : (pagination.current_page - 1) * pagination.per_page + 1;

  const endItem = Math.min(
    pagination.current_page * pagination.per_page,
    pagination.total
  );

  const fieldError = (name) => {
    const messages = formErrors[name];

    if (!messages) return null;

    return (
      <p className="mt-1 text-sm text-red-600">
        {Array.isArray(messages) ? messages[0] : messages}
      </p>
    );
  };

  const inputClass =
    "mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20";

  const labelClass = "block text-sm font-medium text-foreground";

  const closeDetails = () => {
    setIsDetailsOpen(false);
    setSelectedDepartment(null);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Sidebar
        collapsed={sidebarCollapsed}
        onCollapse={setSidebarCollapsed}
      />

      <div
        className={`min-h-screen transition-all duration-300 ${
          sidebarCollapsed ? "ml-20" : "ml-64"
        }`}
      >
        <Header
          onMenuClick={() => setSidebarCollapsed((value) => !value)}
        />

        <main className="p-4 md:p-6 lg:p-8">
          <Breadcrumb
            items={[
              { label: "Home", path: "/" },
              { label: "Department Management" },
            ]}
          />

          {/* Page heading */}
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
                Department Management
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Manage departments, managers, employee counts, locations, and
                budgets.
              </p>
            </div>

            <Button
              variant="default"
              onClick={openCreateForm}
              iconName="Plus"
              iconPosition="left"
            >
              Add Department
            </Button>
          </div>

          {/* Notifications */}
          {notice && (
            <div
              role="status"
              className="mb-5 flex items-start justify-between gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800"
            >
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle2" size={18} />
                <span>{notice}</span>
              </div>

              <button
                type="button"
                onClick={() => setNotice("")}
                aria-label="Dismiss notification"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="mb-5 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
            >
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={18} />
                <span>{error}</span>
              </div>

              <button
                type="button"
                onClick={() => setError("")}
                aria-label="Dismiss error"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          )}

          {/* Statistics */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-border bg-card p-5 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-muted-foreground">
                      {stat.label}
                    </p>

                    <p className="mt-3 break-words text-2xl font-bold">
                      {statsLoading ? "…" : stat.value}
                    </p>

                    <p className="mt-2 text-xs text-muted-foreground">
                      {stat.description}
                    </p>
                  </div>

                  <div className="rounded-lg bg-primary/10 p-3 text-primary">
                    <Icon name={stat.icon} size={22} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Table and filters */}
          <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="border-b border-border p-4 md:p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-lg font-semibold">
                    All Departments
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Department records are loaded from your Laravel backend.
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <div className="relative min-w-0 sm:w-72">
                    <Icon
                      name="Search"
                      size={18}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />

                    <input
                      type="search"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search departments..."
                      aria-label="Search departments"
                      className={`${inputClass} !mt-0 pl-10`}
                    />
                  </div>

                  <select
                    value={statusFilter}
                    onChange={handleStatusChange}
                    aria-label="Filter by status"
                    className={`${inputClass} !mt-0 sm:w-40`}
                  >
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px] text-left text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-5 py-4 font-semibold">Department</th>
                    <th className="px-5 py-4 font-semibold">Manager</th>
                    <th className="px-5 py-4 font-semibold">Location</th>
                    <th className="px-5 py-4 text-right font-semibold">
                      Employees
                    </th>
                    <th className="px-5 py-4 text-right font-semibold">
                      Budget
                    </th>
                    <th className="px-5 py-4 font-semibold">Status</th>
                    <th className="px-5 py-4 font-semibold">Created</th>
                    <th className="px-5 py-4 text-right font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-border">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-5 py-16 text-center text-muted-foreground"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Icon
                            name="LoaderCircle"
                            size={20}
                            className="animate-spin"
                          />
                          Loading departments...
                        </span>
                      </td>
                    </tr>
                  ) : departments.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <Icon
                            name="Building2"
                            size={36}
                            className="mb-3 text-muted-foreground"
                          />
                          <p className="font-medium">No departments found</p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Create a department or change your search filters.
                          </p>

                          <button
                            type="button"
                            onClick={openCreateForm}
                            className="mt-4 font-medium text-primary hover:underline"
                          >
                            Add your first department
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    departments.map((department) => (
                      <tr
                        key={department.id}
                        className="transition hover:bg-muted/30"
                      >
                        <td className="px-5 py-4">
                          <button
                            type="button"
                            onClick={() => openDetails(department)}
                            className="text-left font-semibold hover:text-primary"
                          >
                            {department.name}
                          </button>

                          <p className="mt-1 text-xs text-muted-foreground">
                            Code: {department.code}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <p>{department.manager_name || "Not assigned"}</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {department.manager_email || "—"}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          {department.location || "—"}
                        </td>

                        <td className="px-5 py-4 text-right tabular-nums">
                          {Number(department.employees_count || 0).toLocaleString(
                            "en-IN"
                          )}
                        </td>

                        <td className="px-5 py-4 text-right tabular-nums">
                          {formatCurrency(department.budget)}
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                              department.status === "active"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {department.status === "active"
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </td>

                        <td className="px-5 py-4 text-muted-foreground">
                          {formatDate(department.created_at)}
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-1">
                            <button
                              type="button"
                              title="View department"
                              aria-label={`View ${department.name}`}
                              onClick={() => openDetails(department)}
                              className="rounded-lg p-2 hover:bg-muted"
                            >
                              <Icon name="Eye" size={17} />
                            </button>

                            <button
                              type="button"
                              title="Edit department"
                              aria-label={`Edit ${department.name}`}
                              onClick={() => openEditForm(department)}
                              className="rounded-lg p-2 hover:bg-muted"
                            >
                              <Icon name="Pencil" size={17} />
                            </button>

                            <button
                              type="button"
                              title="Delete department"
                              aria-label={`Delete ${department.name}`}
                              onClick={() => askDelete(department)}
                              className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                            >
                              <Icon name="Trash2" size={17} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col gap-4 border-t border-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span>
                  Showing {startItem}–{endItem} of {pagination.total}
                </span>

                <select
                  value={perPage}
                  onChange={handlePerPageChange}
                  aria-label="Rows per page"
                  className="rounded-md border border-border bg-background px-2 py-1.5 text-foreground"
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage <= 1 || loading}
                  onClick={() => setCurrentPage((page) => page - 1)}
                  className="rounded-lg border border-border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>

                <span className="px-2 text-sm text-muted-foreground">
                  Page {pagination.current_page} of {pagination.last_page}
                </span>

                <button
                  type="button"
                  disabled={
                    currentPage >= pagination.last_page || loading
                  }
                  onClick={() => setCurrentPage((page) => page + 1)}
                  className="rounded-lg border border-border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>

      {/* CREATE / EDIT MODAL */}
      {isFormOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-3 sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeForm();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="department-form-title"
            className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-card shadow-2xl"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-5 py-4">
              <div>
                <h2
                  id="department-form-title"
                  className="text-xl font-semibold"
                >
                  {editingDepartment ? "Edit Department" : "Add Department"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Complete the department details below.
                </p>
              </div>

              <button
                type="button"
                onClick={closeForm}
                disabled={saving}
                aria-label="Close form"
                className="rounded-lg p-2 hover:bg-muted"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5">
              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                >
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="department-name" className={labelClass}>
                    Department Name *
                  </label>
                  <input
                    id="department-name"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    className={inputClass}
                    placeholder="e.g. Information Technology"
                    maxLength={255}
                    required
                  />
                  {fieldError("name")}
                </div>

                <div>
                  <label htmlFor="department-code" className={labelClass}>
                    Department Code *
                  </label>
                  <input
                    id="department-code"
                    name="code"
                    value={form.code}
                    onChange={handleFormChange}
                    className={inputClass}
                    placeholder="e.g. IT"
                    maxLength={50}
                    required
                  />
                  {fieldError("code")}
                </div>

                <div>
                  <label htmlFor="manager-name" className={labelClass}>
                    Manager Name
                  </label>
                  <input
                    id="manager-name"
                    name="manager_name"
                    value={form.manager_name || ""}
                    onChange={handleFormChange}
                    className={inputClass}
                    placeholder="Enter manager name"
                    maxLength={255}
                  />
                  {fieldError("manager_name")}
                </div>

                <div>
                  <label htmlFor="manager-email" className={labelClass}>
                    Manager Email
                  </label>
                  <input
                    id="manager-email"
                    name="manager_email"
                    type="email"
                    value={form.manager_email || ""}
                    onChange={handleFormChange}
                    className={inputClass}
                    placeholder="manager@example.com"
                    maxLength={255}
                  />
                  {fieldError("manager_email")}
                </div>

                <div>
                  <label htmlFor="department-location" className={labelClass}>
                    Location
                  </label>
                  <input
                    id="department-location"
                    name="location"
                    value={form.location || ""}
                    onChange={handleFormChange}
                    className={inputClass}
                    placeholder="e.g. Patna"
                    maxLength={255}
                  />
                  {fieldError("location")}
                </div>

                <div>
                  <label htmlFor="department-status" className={labelClass}>
                    Status *
                  </label>
                  <select
                    id="department-status"
                    name="status"
                    value={form.status}
                    onChange={handleFormChange}
                    className={inputClass}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {fieldError("status")}
                </div>

                <div>
                  <label htmlFor="employees-count" className={labelClass}>
                    Employee Count
                  </label>
                  <input
                    id="employees-count"
                    name="employees_count"
                    type="number"
                    min="0"
                    step="1"
                    value={form.employees_count}
                    onChange={handleFormChange}
                    className={inputClass}
                  />
                  {fieldError("employees_count")}
                </div>

                <div>
                  <label htmlFor="department-budget" className={labelClass}>
                    Budget (INR)
                  </label>
                  <input
                    id="department-budget"
                    name="budget"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.budget}
                    onChange={handleFormChange}
                    className={inputClass}
                  />
                  {fieldError("budget")}
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor="department-description"
                    className={labelClass}
                  >
                    Description
                  </label>
                  <textarea
                    id="department-description"
                    name="description"
                    value={form.description || ""}
                    onChange={handleFormChange}
                    rows={4}
                    className={inputClass}
                    placeholder="Describe the department's responsibilities..."
                  />
                  {fieldError("description")}
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="default"
                  disabled={saving}
                  iconName={saving ? "LoaderCircle" : "Save"}
                  iconPosition="left"
                >
                  {saving
                    ? "Saving..."
                    : editingDepartment
                    ? "Update Department"
                    : "Create Department"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DEPARTMENT DETAILS MODAL */}
      {isDetailsOpen && selectedDepartment && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-3 sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeDetails();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="department-details-title"
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-card shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-border p-5">
              <div>
                <h2
                  id="department-details-title"
                  className="text-xl font-semibold"
                >
                  Department Details
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {selectedDepartment.code}
                </p>
              </div>

              <button
                type="button"
                onClick={closeDetails}
                aria-label="Close details"
                className="rounded-lg p-2 hover:bg-muted"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-5 p-5">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-2xl font-bold">
                    {selectedDepartment.name}
                  </h3>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      selectedDepartment.status === "active"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {selectedDepartment.status}
                  </span>
                </div>

                <p className="mt-2 text-sm text-muted-foreground">
                  {selectedDepartment.description || "No description provided."}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                  ["Department Code", selectedDepartment.code],
                  ["Manager", selectedDepartment.manager_name],
                  ["Manager Email", selectedDepartment.manager_email],
                  ["Location", selectedDepartment.location],
                  [
                    "Employees",
                    Number(selectedDepartment.employees_count || 0).toLocaleString(
                      "en-IN"
                    ),
                  ],
                  ["Budget", formatCurrency(selectedDepartment.budget)],
                  ["Created", formatDate(selectedDepartment.created_at)],
                  ["Last Updated", formatDate(selectedDepartment.updated_at)],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-lg border border-border p-4"
                  >
                    <p className="text-xs font-medium text-muted-foreground">
                      {label}
                    </p>
                    <p className="mt-2 break-words font-medium">
                      {value || "—"}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 border-t border-border pt-5">
                <Button variant="outline" onClick={closeDetails}>
                  Close
                </Button>

                <Button
                  variant="default"
                  onClick={() => {
                    closeDetails();
                    openEditForm(selectedDepartment);
                  }}
                >
                  <Icon name="Pencil" size={16} />
                  <span className="ml-2">Edit Department</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {isDeleteOpen && departmentToDelete && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-department-title"
            className="w-full max-w-md rounded-xl bg-card p-6 shadow-2xl"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
              <Icon name="Trash2" size={23} />
            </div>

            <h2
              id="delete-department-title"
              className="text-xl font-semibold"
            >
              Delete Department?
            </h2>

            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              Are you sure you want to delete{" "}
              <strong className="text-foreground">
                {departmentToDelete.name}
              </strong>
              ? This action cannot be undone.
            </p>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                disabled={deleting}
                onClick={() => {
                  setIsDeleteOpen(false);
                  setDepartmentToDelete(null);
                }}
              >
                Cancel
              </Button>

              <Button
                variant="destructive"
                disabled={deleting}
                onClick={handleDelete}
              >
                {deleting ? "Deleting..." : "Delete Department"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagement;